export enum UserRole {
  NATIONAL = 'NATIONAL',
  STATE = 'STATE',
  DISCOM = 'DISCOM',
  CONSUMER = 'CONSUMER',
  VENDOR = 'VENDOR',
}

export enum AlarmSeverity {
  CRITICAL = 'CRITICAL',
  WARNING = 'WARNING',
  INFO = 'INFO',
}

export enum PlantStatus {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
  WARNING = 'WARNING',
  FAULT = 'FAULT',
  INACTIVE = 'INACTIVE',
}

export enum TicketStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
}

export type UnitScope = 'consumer' | 'district' | 'discom' | 'state' | 'national';

export interface GenerationDataPoint {
  label: string;
  value: number;
  highlighted?: boolean;
}

export interface MonthlyDataPoint {
  month: string;
  generated: number;
  exported: number;
}

export interface HourlyDataPoint {
  [key: string]: string | number;
  time: string;
  power: number;
}

export interface VoltageDataPoint {
  [key: string]: string | number;
  time: string;
  voltage: number;
}

export interface StateData {
  id: string;
  name: string;
  plants: number;
  capacityGWp: number;
  generationGWh: number;
  prPercent: number;
  cufPercent: number;
  activePercent: number;
  alarms: number;
  momTrend: number;
  status: 'online' | 'warning' | 'offline';
}

export interface DistrictData {
  id: string;
  name: string;
  plants: number;
  generationGWh: number;
  prPercent: number;
  status: 'online' | 'warning' | 'offline';
}

export interface DISCOMData {
  id: string;
  name: string;
  state: string;
  plants: number;
  capacityGWp: number;
  generationGWh: number;
  prPercent: number;
  activePercent: number;
  alarms: number;
}

export interface CircleData {
  id: string;
  name: string;
  plants: number;
  generationMWh: number;
  prPercent: number;
  activePercent: number;
  alarms: number;
  mttrHours: number;
}

export interface Alarm {
  id: string;
  severity: AlarmSeverity;
  description: string;
  parameter: string;
  plantId: string;
  state: string;
  timestamp: string;
  duration: string;
  status: 'ACTIVE' | 'RESOLVED';
  faultType: string;
}

export interface ServiceTicket {
  id: string;
  plantId: string;
  consumerName: string;
  issueType: string;
  description: string;
  raisedDate: string;
  assignedTo: string;
  assignedVendor: string;
  status: TicketStatus;
  resolutionTime?: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
}

export interface RMSDevice {
  imei: string;
  plantId: string;
  status: PlantStatus;
  firmwareVersion: string;
  certExpiry: string;
  lastSeen: string;
  daysToExpiry: number;
}

export interface ElectricalParams {
  dcVoltage1: number;
  dcCurrent1: number;
  dcPower1: number;
  dcVoltage2: number;
  dcCurrent2: number;
  dcPower2: number;
  acVoltage: number;
  acCurrent: number;
  frequency: number;
  powerFactor: number;
  reactivePower: number;
  apparentPower: number;
  inverterTemp: number;
  inverterStatus: 'ON' | 'OFF' | 'FAULT';
  onTimeHours: number;
  activePower: number;
}

export interface ConsumerPlant {
  id: string;
  consumerName: string;
  address: string;
  capacityKWp: number;
  inverterBrand: string;
  discom: string;
  state: string;
  district: string;
  commissionDate: string;
  status: PlantStatus;
  livePowerKW: number;
  todayKWh: number;
  todaySavings: number;
  prPercent: number;
  lifetimeKWh: number;
  lifetimeSavings: number;
  co2OffsetMT: number;
  healthScore: number;
  plantAvailabilityPct: number;
  specificYieldMonthly: number;
  electrical: ElectricalParams;
  prNeighbours: { label: string; value: number; isMe: boolean }[];
}

export interface VendorPlant {
  id: string;
  consumerName: string;
  consumerNumber: string;
  mobile: string;
  meterNumber: string;
  state: string;
  district: string;
  town: string;
  pincode: string;
  village: string;
  circle: string;
  division: string;
  subDivision: string;
  inverterBrand: string;
  status: PlantStatus;
  todayKWh: number;
  prPercent: number;
  prTrend7d: number[];
  baselinePR90d: number;
  peerPRAvg: number;
  cufPercent: number;
  specificYieldMonthly: number;
  availabilityPercent: number;
  lastSeen: string;
  activeAlarms: number;
  lastAlarmText: string | null;
  ticketStatus: TicketStatus | null;
  openTickets: number;
  commissionStatus: 'COMMISSIONED' | 'PENDING' | 'UNDER_TEST' | 'INACTIVE';
  capacityKWp: number;
  plantAgeYears: number;
  hasFaultFlag: boolean;
  faultCode: string | null;
  rmsOfflineMinutes: number | null;
  inverterSilentMinutes: number | null;
  ghiBelowNorm: boolean;
}

export interface FaultDistribution {
  name: string;
  value: number;
  color: string;
}

export interface User {
  name: string;
  role: UserRole;
  org: string;
  scope: string;
}

export interface BillingCycle {
  month: string;
  exportKWh: number;
  importKWh: number;
  estimatedCredit: number;
}

export interface FeederData {
  id: string;
  name: string;
  circle: string;
  generationMW: number;
  plants: number;
  penetrationPercent: number;
  avgVoltage: number;
  alarms: number;
}

export interface VarianceAlert {
  plantId: string;
  consumerName: string;
  inverterKWh: number;
  meterKWh: number;
  variancePercent: number;
  status: 'REVIEW' | 'CLEAR';
}

export interface CommandLog {
  timestamp: string;
  command: string;
  issuedBy: string;
  plantId: string;
  result: 'SUCCESS' | 'FAILED' | 'PENDING';
}
